import { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/Icons';
import { db } from '../../firebase'; 
import { 
  collection, query, orderBy, doc, updateDoc, deleteDoc, 
  limit, getDocs, startAfter, endBefore, getCountFromServer, serverTimestamp, addDoc,
  limitToLast // limitToLast 추가
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext'; 

const InquiryManagementPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth(); 

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7; 
  const [lastVisibleDocOfCurrentPage, setLastVisibleDocOfCurrentPage] = useState(null);
  const [firstVisibleDocOfCurrentPage, setFirstVisibleDocOfCurrentPage] = useState(null); 
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalInquiries, setTotalInquiries] = useState(0);
  const [pageDirection, setPageDirection] = useState('initial'); 

  useEffect(() => {
    const getTotalInquiries = async () => {
      try {
        const coll = collection(db, "inquiries");
        const snapshot = await getCountFromServer(coll);
        setTotalInquiries(snapshot.data().count);
      } catch (error) {
        console.error("Error fetching total inquiry count: ", error);
      }
    };
    getTotalInquiries();
  }, []);
  
  useEffect(() => {
    const fetchInquiries = async () => {
      setLoading(true);
      try {
        let q;
        const inquiriesCollectionRef = collection(db, "inquiries");

        if (pageDirection === 'initial' || currentPage === 1) {
          q = query(inquiriesCollectionRef, orderBy("submittedAt", "desc"), limit(itemsPerPage));
        } else if (pageDirection === 'next' && lastVisibleDocOfCurrentPage) {
          q = query(inquiriesCollectionRef, orderBy("submittedAt", "desc"), startAfter(lastVisibleDocOfCurrentPage), limit(itemsPerPage));
        } else if (pageDirection === 'prev' && firstVisibleDocOfCurrentPage) {
          q = query(inquiriesCollectionRef, orderBy("submittedAt", "desc"), endBefore(firstVisibleDocOfCurrentPage), limitToLast(itemsPerPage));
        } else {
          q = query(inquiriesCollectionRef, orderBy("submittedAt", "desc"), limit(itemsPerPage));
        }

        const documentSnapshots = await getDocs(q);
        let newInquiries = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setInquiries(newInquiries);

        if (documentSnapshots.docs.length > 0) {
          setFirstVisibleDocOfCurrentPage(documentSnapshots.docs[0]); 
          setLastVisibleDocOfCurrentPage(documentSnapshots.docs[documentSnapshots.docs.length - 1]); 
          
          const potentialNextQuery = query(inquiriesCollectionRef, orderBy("submittedAt", "desc"), startAfter(documentSnapshots.docs[documentSnapshots.docs.length - 1]), limit(1));
          const nextSnapshot = await getDocs(potentialNextQuery);
          setIsLastPage(nextSnapshot.empty);
        } else {
          if (pageDirection === 'next') setIsLastPage(true);
          if (pageDirection === 'prev' && currentPage === 1) {
             setFirstVisibleDocOfCurrentPage(null);
             setLastVisibleDocOfCurrentPage(null);
             setIsLastPage(totalInquiries <= itemsPerPage); 
          }
        }
      } catch (error) {
        console.error("Error fetching inquiries: ", error);
      }
      setLoading(false);
      setPageDirection('idle'); 
    };
    
    if (totalInquiries > 0 || (pageDirection === 'initial' && currentPage === 1)) {
        fetchInquiries();
    } else if (totalInquiries === 0) {
        setInquiries([]);
        setLoading(false);
        setIsLastPage(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, totalInquiries]);


  const handleStatusChange = async (id, inquiryName, oldStatus, newStatus) => { 
    if (oldStatus === newStatus) return; 

    const inquiryRef = doc(db, "inquiries", id);
    try {
      await updateDoc(inquiryRef, { status: newStatus });
      
      try {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + 90);

        await addDoc(collection(db, "activities"), {
          type: "inquiry_status_change",
          text: `문의 "${inquiryName}" 상태 변경: ${oldStatus} → ${newStatus}`,
          timestamp: serverTimestamp(),
          expireAt: expireDate, // TTL 필드 추가
          relatedId: id,
          userId: currentUser ? currentUser.uid : null,
          details: { inquiryId: id, inquiryName: inquiryName, oldStatus: oldStatus, newStatus: newStatus }
        });
      } catch (activityError) {
        console.error("Error logging inquiry status change activity: ", activityError);
      }

      setInquiries(prevInquiries => prevInquiries.map(inq => 
        inq.id === id ? { ...inq, status: newStatus } : inq
      ));
      if (selectedInquiry && selectedInquiry.id === id) {
        setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
      }
      alert(`문의 ID ${id} 상태를 '${newStatus}'(으)로 변경했습니다.`);
    } catch (error) {
      console.error("Error updating inquiry status: ", error);
      alert(`문의 상태 변경 실패: ${error.message}`);
    }
  };
  
  const handleDelete = async (id, inquiryName) => { 
    if (window.confirm("정말로 이 문의를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) { 
      try {
        await deleteDoc(doc(db, "inquiries", id));
        
        try {
            const expireDate = new Date();
            expireDate.setDate(expireDate.getDate() + 90);

            await addDoc(collection(db, "activities"), {
              type: "inquiry_delete",
              text: `문의 삭제: "${inquiryName}"`,
              timestamp: serverTimestamp(),
              expireAt: expireDate, // TTL 필드 추가
              relatedId: id,
              userId: currentUser ? currentUser.uid : null,
              details: { inquiryId: id, inquiryName: inquiryName }
            });
        } catch (activityError) {
            console.error("Error logging inquiry delete activity: ", activityError);
        }

        alert(`문의 ID ${id} 삭제 완료.`);
        const newTotal = totalInquiries - 1;
        setTotalInquiries(newTotal >= 0 ? newTotal : 0);

        if (inquiries.length === 1 && currentPage > 1) {
            setPageDirection('prev'); 
            setCurrentPage(prev => prev - 1);
        } else {
            setPageDirection('initial'); 
            if (currentPage === 1) {
                // totalInquiries 변경으로 useEffect가 실행됨
            }
        }
        setSelectedInquiry(null); 
      } catch (error) {
        console.error("Error deleting inquiry: ", error);
        alert(`문의 삭제 실패: ${error.message}`);
      }
    }
  };
  
  const totalPages = Math.ceil(totalInquiries / itemsPerPage);

  const handleNextPage = () => {
    if (!isLastPage && currentPage < totalPages && !loading) {
        setPageDirection('next');
        setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      setPageDirection('prev');
      setCurrentPage(prev => prev - 1);
    }
  };

  if (loading && inquiries.length === 0 && pageDirection === 'initial' && currentPage === 1) { 
    return (
      <>
        <title>문의 관리 - 관리자</title>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">문의 관리</h1>
          <p className="text-center text-gray-500 dark:text-gray-400">문의 목록을 불러오는 중입니다...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <title>문의 관리 - 관리자</title>
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">문의 관리</h1>
        <div className="bg-white dark:bg-slate-800 shadow-md rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">이름</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">이메일</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">내용 (요약)</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">접수일</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">상태</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">작업</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
              {loading && inquiries.length > 0 && (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">페이지 로딩 중...</td></tr>
              )}
              {!loading && inquiries.length === 0 && (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">접수된 문의가 없습니다.</td></tr>
              )}
              {!loading && inquiries.map(inquiry => ( 
                <tr key={inquiry.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{inquiry.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{inquiry.email || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 max-w-xs truncate" title={inquiry.message}>{inquiry.message}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {inquiry.submittedAt?.toDate ? inquiry.submittedAt.toDate().toLocaleString() : '날짜 정보 없음'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      inquiry.status === '신규' ? 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100' :
                      inquiry.status === '확인중' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-100' :
                      'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100'
                    }`}>
                      {inquiry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button onClick={() => setSelectedInquiry(inquiry)} className="text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">보기</button>
                    <button onClick={() => handleDelete(inquiry.id, inquiry.name)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button onClick={handlePrevPage} disabled={currentPage === 1 || loading} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50">
                <ChevronLeftIcon /> 이전
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                페이지 {currentPage} / {totalPages > 0 ? totalPages : 1}
              </span>
              <button onClick={handleNextPage} disabled={isLastPage || currentPage === totalPages || loading} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50">
                다음 <ChevronRightIcon />
              </button>
            </nav>
          </div>
        )}

        {selectedInquiry && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-white">문의 상세 정보</h2>
              <div className="space-y-3 mb-4 overflow-y-auto">
                <p className="dark:text-gray-300"><strong>이름:</strong> {selectedInquiry.name}</p>
                <p className="dark:text-gray-300"><strong>이메일:</strong> {selectedInquiry.email || 'N/A'}</p>
                <p className="dark:text-gray-300"><strong>연락처:</strong> {selectedInquiry.phone}</p>
                <p className="dark:text-gray-300"><strong>접수일:</strong> {selectedInquiry.submittedAt?.toDate ? selectedInquiry.submittedAt.toDate().toLocaleString() : 'N/A'}</p>
                <p className="dark:text-gray-300"><strong>상태:</strong> 
                  <select 
                    value={selectedInquiry.status} 
                    onChange={(e) => handleStatusChange(selectedInquiry.id, selectedInquiry.name, selectedInquiry.status, e.target.value)}
                    className="ml-2 p-1 border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white"
                  >
                    <option>신규</option>
                    <option>확인중</option>
                    <option>답변완료</option>
                  </select>
                </p>
                <p className="dark:text-gray-300"><strong>내용:</strong></p>
                <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md whitespace-pre-line text-gray-700 dark:text-gray-300">{selectedInquiry.message}</div>
              </div>
              <div className="mt-auto flex justify-end space-x-3 pt-4 border-t dark:border-slate-700">
                <button onClick={() => setSelectedInquiry(null)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md">닫기</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default InquiryManagementPage;
